import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function SolutionsPage() {
  const { t } = useTranslation();
  const [selectedSolution, setSelectedSolution] = useState<number | null>(null);

  const solutions = [
    {
      icon: 'ri-government-line',
      title: t('solutions.card1.title'),
      desc: t('solutions.card1.desc'),
      details: t('solutions.card1.details'),
      image: 'https://public.readdy.ai/ai/img_res/efd08989173103f8079a5f69b7474734.jpg'
    },
    {
      icon: 'ri-bank-line',
      title: t('solutions.card2.title'),
      desc: t('solutions.card2.desc'),
      details: t('solutions.card2.details'),
      image: 'https://readdy.ai/api/search-image?query=stunning%20modern%20skyscrapers%20financial%20district%20glass%20towers%20reflecting%20blue%20sky%20contemporary%20urban%20architecture%20sleek%20corporate%20buildings%20downtown%20cityscape%20professional%20business%20environment%20tall%20elegant%20structures%20geometric%20patterns%20clean%20minimal%20aesthetic%20upward%20perspective%20showing%20height%20and%20scale&width=600&height=400&seq=bank-skyscrapers-001&orientation=landscape'
    },
    {
      icon: 'ri-building-line',
      title: t('solutions.card3.title'),
      desc: t('solutions.card3.desc'),
      details: t('solutions.card3.details'),
      image: 'https://readdy.ai/api/search-image?query=luxurious%20modern%20corporate%20office%20interior%20spacious%20open%20workspace%20with%20elegant%20furniture%20contemporary%20design%20natural%20lighting%20glass%20walls%20minimalist%20aesthetic%20professional%20business%20environment%20sleek%20desks%20comfortable%20seating%20clean%20organized%20premium%20quality%20corporate%20headquarters%20sophisticated%20style&width=600&height=400&seq=corp-office-luxury-001&orientation=landscape'
    },
    {
      icon: 'ri-road-map-line',
      title: t('solutions.card4.title'),
      desc: t('solutions.card4.desc'),
      details: t('solutions.card4.details'),
      image: 'https://readdy.ai/api/search-image?query=smart%20city%20infrastructure%20urban%20roads%20with%20modern%20surveillance%20cameras%20traffic%20monitoring%20systems%20intelligent%20transportation%20technology%20street%20lighting%20sensors%20connected%20city%20infrastructure%20clean%20organized%20urban%20environment%20technology%20integration%20wide%20street%20view%20showing%20multiple%20cameras%20and%20monitoring%20equipment%20blue%20hour%20lighting&width=600&height=400&seq=smart-city-roads-001&orientation=landscape'
    },
    {
      icon: 'ri-train-line',
      title: t('solutions.card5.title'),
      desc: t('solutions.card5.desc'),
      details: t('solutions.card5.details'),
      image: 'https://readdy.ai/api/search-image?query=modern%20digital%20display%20screens%20in%20metro%20train%20interior%20passenger%20information%20systems%20electronic%20departure%20boards%20airport%20terminal%20displays%20clean%20contemporary%20transportation%20technology%20LED%20screens%20showing%20schedules%20and%20information%20sleek%20design%20professional%20lighting%20organized%20layout&width=600&height=400&seq=transport-displays-001&orientation=landscape'
    },
    {
      icon: 'ri-store-2-line',
      title: t('solutions.card6.title'),
      desc: t('solutions.card6.desc'),
      details: t('solutions.card6.details'),
      image: 'https://readdy.ai/api/search-image?query=modern%20point%20of%20sale%20terminals%20in%20shopping%20mall%20retail%20checkout%20counters%20with%20touchscreen%20POS%20systems%20payment%20terminals%20sleek%20design%20contemporary%20retail%20technology%20clean%20organized%20supermarket%20checkout%20area%20professional%20lighting%20multiple%20payment%20stations%20modern%20retail%20environment&width=600&height=400&seq=retail-pos-terminals-001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <h1 className="text-5xl lg:text-6xl font-bold text-black mb-8">
            {t('solutions.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            {t('solutions.intro')}
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                onClick={() => setSelectedSolution(index)}
                className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                <div className="h-56 w-full overflow-hidden">
                  <img 
                    src={solution.image} 
                    alt={solution.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${solution.icon} text-4xl text-[#1E3A8A]`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-3">{solution.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4">{solution.desc}</p>
                  <div className="flex items-center gap-2 text-[#1E3A8A] group-hover:gap-3 transition-all">
                    <span className="text-sm font-medium whitespace-nowrap">{t('solutions.learnMore')}</span>
                    <i className="ri-arrow-right-line text-lg"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Details Modal */}
      {selectedSolution !== null && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedSolution(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedSolution(null)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            <div className="w-14 h-14 flex items-center justify-center mb-6">
              <i className={`${solutions[selectedSolution].icon} text-5xl text-[#1E3A8A]`}></i>
            </div>
            
            <h3 className="text-3xl font-bold text-black mb-4">
              {solutions[selectedSolution].title}
            </h3>
            
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {solutions[selectedSolution].desc}
            </p>
            
            <div className="text-base text-gray-700 leading-relaxed">
              {solutions[selectedSolution].details}
            </div>
            
            <div className="mt-8 pt-6 border-t border-gray-200">
              <a 
                href="/contacts"
                className="inline-flex items-center gap-3 px-6 py-3 bg-[#1E3A8A] text-white rounded-md font-medium hover:bg-[#1E3A8A]/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('solutions.contactUs')}
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
